import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  Slide,
  Card,
  CardContent,
  TextField,
} from "@mui/material";
import { Add } from "@mui/icons-material";

const AddSkillModal = ({ open, handleClose, handleSave, tableApis }) => {
  const [skillInputs, setSkillInputs] = useState([{ id: 1, value: "" }]);

  // Handle adding more skill inputs
  const handleAddMoreSkill = () => {
    const newId =
      skillInputs.length > 0
        ? Math.max(...skillInputs.map((item) => item.id)) + 1
        : 1;
    setSkillInputs([...skillInputs, { id: newId, value: "" }]);
  };

  // Handle skill input change
  const handleSkillInputChange = (id, value) => {
    setSkillInputs(
      skillInputs.map((input) =>
        input.id === id ? { ...input, value } : input
      )
    );
  };

  const handleSaveSkill = async () => {
    try {
      const skillsToAdd = skillInputs.filter(
        (input) => input.value.trim() !== ""
      );

      if (skillsToAdd.length === 0) {
        alert("Please enter at least one skill name");
        return;
      }

      // Save each skill
      for (const skill of skillsToAdd) {
        await tableApis.addSkill({ name: skill.value });
      }

      // Reset form
      setSkillInputs([{ id: 1, value: "" }]);

      // Call the parent's save handler to refresh data and close modal
      handleSave();
    } catch (error) {
      console.error("Error adding skills:", error);
      alert("Failed to add skills. Please try again.");
    }
  };

  return (
    <Modal open={open} onClose={handleClose} closeAfterTransition>
      <Slide direction="left" in={open}>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "85%",
            height: "100%",
            bgcolor: "#f5f5f5",
            boxShadow: 24,
            p: 4,
            overflow: "auto",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography variant="h3" component="h2" sx={{ color: "#212121" }}>
              Add Skills
            </Typography>
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{
                borderColor: "#bdbdbd",
                color: "#424242",
                "&:hover": {
                  borderColor: "#9e9e9e",
                  bgcolor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Close
            </Button>
          </Box>
          <Card
            sx={{
              p: 2,
              bgcolor: "#ffffff",
              boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)",
            }}
          >
            <CardContent>
              <Typography
                variant="h4"
                component="h3"
                sx={{
                  color: "#212121",
                  mb: 3,
                  fontWeight: 400,
                  borderBottom: "1px solid #e0e0e0",
                  paddingBottom: 2,
                }}
              >
                Create New
              </Typography>

              {skillInputs.map((input, index) => (
                <Box key={input.id} mb={3}>
                  <Typography
                    variant="body1"
                    mb={1}
                    sx={{ color: "#212121" }}
                    fontWeight={400}
                  >
                    Skill{" "}
                    {index === 0 && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Skill Name"
                    value={input.value}
                    onChange={(e) =>
                      handleSkillInputChange(input.id, e.target.value)
                    }
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "#e0e0e0",
                        },
                        "&:hover fieldset": {
                          borderColor: "#bdbdbd",
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: "#424242",
                      },
                    }}
                  />
                </Box>
              ))}

              <Box mt={1} mb={4}>
                <Button
                  startIcon={<Add />}
                  onClick={handleAddMoreSkill}
                  sx={{
                    color: "#4285F4",
                    textTransform: "none",
                    fontWeight: 500,
                    padding: "0px",
                    "&:hover": {
                      bgcolor: "transparent",
                      opacity: 0.8,
                    },
                  }}
                >
                  Add More
                </Button>
              </Box>

              <Box
                display="flex"
                mt={4}
                gap={2}
                borderTop="1px solid #e0e0e0"
                pt={3}
              >
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#4285F4",
                    color: "white",
                    "&:hover": { bgcolor: "#3367D6" },
                    textTransform: "none",
                    fontWeight: 500,
                    paddingX: 4,
                    paddingY: 1,
                  }}
                  startIcon={<span style={{ fontSize: "16px" }}>✓</span>}
                  onClick={handleSaveSkill}
                >
                  Save
                </Button>
                <Button
                  variant="text"
                  onClick={handleClose}
                  sx={{
                    color: "#757575",
                    "&:hover": {
                      bgcolor: "rgba(0, 0, 0, 0.04)",
                    },
                    textTransform: "none",
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Slide>
    </Modal>
  );
};

export default AddSkillModal;
